import { useState, useEffect, useRef } from 'react';

interface UseDelayedSkeletonOptions {
  loading: boolean;
  skeletonDelay?: number;
  minDisplayTime?: number;
}

export const useDelayedSkeleton = ({
  loading,
  skeletonDelay = 200,
  minDisplayTime = 1000
}: UseDelayedSkeletonOptions) => {
  const [showSkeleton, setShowSkeleton] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [skeletonWasShown, setSkeletonWasShown] = useState(false);
  const skeletonStartTime = useRef<number | null>(null);
  const skeletonTimeout = useRef<NodeJS.Timeout | null>(null);
  const contentTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (loading) {
      setShowContent(false);
      setShowSkeleton(false);
      setSkeletonWasShown(false);
      skeletonStartTime.current = null;

      if (skeletonTimeout.current) clearTimeout(skeletonTimeout.current);
      if (contentTimeout.current) clearTimeout(contentTimeout.current);

      skeletonTimeout.current = setTimeout(() => {
        setShowSkeleton(true);
        setSkeletonWasShown(true);
        skeletonStartTime.current = Date.now();
      }, skeletonDelay);
    } else {
      if (skeletonTimeout.current) {
        clearTimeout(skeletonTimeout.current);
        skeletonTimeout.current = null;
      }

      if (showSkeleton && skeletonStartTime.current) {
        const elapsed = Date.now() - skeletonStartTime.current;
        const remaining = Math.max(0, minDisplayTime - elapsed);
        
        contentTimeout.current = setTimeout(() => {
          setShowSkeleton(false);
          setShowContent(true);
        }, remaining);
      } else {
        setShowContent(true);
      }
    }

    return () => {
      if (skeletonTimeout.current) clearTimeout(skeletonTimeout.current);
      if (contentTimeout.current) clearTimeout(contentTimeout.current);
    };
  }, [loading, skeletonDelay, minDisplayTime]);

  return {
    showSkeleton,
    showContent: showContent && !loading,
    skeletonWasShown
  };
};
